import { useCallback, useEffect, useRef, useState } from 'react';

import { IUser } from '../../../../models/user/IUser';
import { readStandaloneDisplayModeFromWindow } from '../../utils/isStandaloneDisplayMode';
import { subscribePwaPush } from '../api/subscribePwaPush';
import { IPwaPushApiResult } from '../types';
import { createPushSubscription } from '../utils/createPushSubscription';
import {
    hasSyncedPushSubscription,
    markPushPromptDismissed,
    markPushSubscriptionSynced,
    shouldShowPushPermissionPrompt,
} from '../utils/pwaPushLocalState';

export interface IUsePwaPushNotificationsOptions {
    user: IUser | undefined;
    token: string | undefined;
    win?: Window;
    isStandalone?: boolean;
    subscribeFn?: typeof subscribePwaPush;
    createSubscriptionFn?: typeof createPushSubscription;
}

export interface IUsePwaPushNotificationsResult {
    permissionDialogOpen: boolean;
    isSyncing: boolean;
    lastResult: IPwaPushApiResult | null;
    enablePush: () => Promise<void>;
    dismissPermissionPrompt: () => void;
}

/** Some TS DOM libs omit Notification on Window — keep access explicit for build. */
type WindowWithNotification = Window & {
    Notification?: {
        permission: NotificationPermission;
        requestPermission: () => Promise<NotificationPermission>;
    };
};

function readNotificationPermission(win: Window): NotificationPermission | 'unsupported' {
    const notificationApi = (win as WindowWithNotification).Notification;
    if (!notificationApi) {
        return 'unsupported';
    }
    return notificationApi.permission;
}

/**
 * Soft-prompts for notification permission in standalone PWA,
 * then syncs the Web Push subscription to the backend.
 *
 * Closing the prompt only silences it for PWA_PUSH_PROMPT_COOLDOWN_MS; the next mount
 * after that window re-opens it as long as permission is still `default`.
 */
export function usePwaPushNotifications(options: IUsePwaPushNotificationsOptions): IUsePwaPushNotificationsResult {
    const {
        user,
        token,
        win = window,
        isStandalone: isStandaloneOption,
        subscribeFn = subscribePwaPush,
        createSubscriptionFn = createPushSubscription,
    } = options;

    const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastResult, setLastResult] = useState<IPwaPushApiResult | null>(null);
    const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
        readNotificationPermission(win)
    );
    const inFlightRef = useRef(false);

    const isStandalone =
        typeof isStandaloneOption === 'boolean'
            ? isStandaloneOption
            : readStandaloneDisplayModeFromWindow(win);

    const syncSubscription = useCallback(async () => {
        const userId = user?.Userid;
        if (!user || !token || !userId || inFlightRef.current) {
            return;
        }

        inFlightRef.current = true;
        setIsSyncing(true);
        try {
            const subscription = await createSubscriptionFn();
            if (!subscription) {
                setLastResult({ status: 'error', note: 'NoSubscription' });
                return;
            }

            const result = await subscribeFn({ user, token, subscription });
            setLastResult(result);
            if (result.status === 'subscribed') {
                markPushSubscriptionSynced(userId, win.localStorage);
            }
        } finally {
            inFlightRef.current = false;
            setIsSyncing(false);
        }
    }, [user, token, win, subscribeFn, createSubscriptionFn]);

    useEffect(() => {
        const userId = user?.Userid;
        if (!user || !token || !userId) {
            setPermissionDialogOpen(false);
            return;
        }

        if (
            shouldShowPushPermissionPrompt({
                userId,
                isStandalone,
                notificationPermission: permission,
                storage: win.localStorage,
            })
        ) {
            setPermissionDialogOpen(true);
            return;
        }

        setPermissionDialogOpen(false);

        // Already allowed: re-sync once per browser session so reinstall / http_410
        // recovery works even if a stale localStorage "synced" marker remains.
        if (isStandalone && permission === 'granted') {
            const sessionKey = `pwa.push.sessionSync.${userId}`;
            const alreadyTriedThisSession = win.sessionStorage?.getItem(sessionKey) === '1';
            if (!alreadyTriedThisSession || !hasSyncedPushSubscription(userId, win.localStorage)) {
                try {
                    win.sessionStorage?.setItem(sessionKey, '1');
                } catch {
                    // ignore quota / private mode
                }
                void syncSubscription();
            }
        }
    }, [user, token, win, isStandalone, permission, syncSubscription]);

    const enablePush = useCallback(async () => {
        const notificationApi = (win as WindowWithNotification).Notification;
        if (!notificationApi) {
            setPermission('unsupported');
            setLastResult({ status: 'error', note: 'Unsupported' });
            setPermissionDialogOpen(false);
            return;
        }

        const nextPermission = await notificationApi.requestPermission();
        setPermission(nextPermission);
        setPermissionDialogOpen(false);

        if (nextPermission !== 'granted') {
            // Native prompt closed without a decision stays 'default' — start the cooldown
            // so we re-ask later instead of nagging on the next mount.
            if (user?.Userid) {
                markPushPromptDismissed(user.Userid, win.localStorage);
            }
            return;
        }

        await syncSubscription();
    }, [win, user, syncSubscription]);

    const dismissPermissionPrompt = useCallback(() => {
        if (user?.Userid) {
            markPushPromptDismissed(user.Userid, win.localStorage);
        }
        setPermissionDialogOpen(false);
    }, [user, win]);

    return {
        permissionDialogOpen,
        isSyncing,
        lastResult,
        enablePush,
        dismissPermissionPrompt,
    };
}
